import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { api } from "../lib/api";
import { AppError, toUserMessage } from "../lib/errors";
import { storage } from "../lib/storage";
import { UI_TEXT_TONE } from "../lib/uiTone";

const PREFERRED_NAME_KEY = "name";

export default function EmailInviteOnboarding() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preferredName, setPreferredName] = useState("");
  const [profiles, setProfiles] = useState<Array<{ key: string; value: string }>>([]);

  useEffect(() => {
    const auth = storage.getAuth();
    if (!auth.userId || !auth.accessToken) {
      navigate("/splash", { replace: true });
      return;
    }

    let active = true;

    (async () => {
      setLoading(true);
      setError(null);
      try {
        const currentProfiles = await api.getUserProfile();
        if (!active) return;

        const profileValues = currentProfiles.map((item) => ({ key: item.key, value: item.value }));
        const existingPreferredName = currentProfiles.find((item) => item.key === PREFERRED_NAME_KEY)?.value ?? "";

        setProfiles(profileValues);
        setPreferredName(existingPreferredName);
      } catch (errorValue) {
        if (!active) return;
        if (errorValue instanceof AppError && errorValue.code === "SESSION_EXPIRED") {
          navigate("/splash", { replace: true });
          return;
        }
        setError(toUserMessage(errorValue));
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    })();

    return () => {
      active = false;
    };
  }, [navigate]);

  const trimmedPreferredName = preferredName.trim();
  const canSubmit = trimmedPreferredName.length > 0 && !saving;
  const helperText = useMemo(
    () => "メイがあなたを呼ぶときの名前です。あとから変更できます。",
    []
  );

  const handleSave = async () => {
    if (!canSubmit) return;
    setSaving(true);
    setError(null);
    try {
      const nextProfiles = profiles.filter((item) => item.key !== PREFERRED_NAME_KEY);
      nextProfiles.push({ key: PREFERRED_NAME_KEY, value: trimmedPreferredName });
      await api.updateUserProfile({ profiles: nextProfiles });
      navigate("/home", { replace: true });
    } catch (errorValue) {
      setError(toUserMessage(errorValue));
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className="min-h-screen overflow-x-hidden bg-[radial-gradient(circle_at_top,_rgba(244,227,215,0.9),_rgba(253,248,245,1)_55%)] px-4 py-6 text-[#4A3E39] sm:px-6 sm:py-10">
      <div className="mx-auto flex min-h-[calc(100vh-5rem)] max-w-md items-center justify-center">
        <div className="w-full rounded-[2.4rem] border border-white/80 bg-white/90 p-6 shadow-[0_24px_60px_rgba(124,90,78,0.16)] backdrop-blur-sm sm:p-7">
          <div className="mb-6">
            <p className={`text-[11px] font-bold tracking-[0.12em] ${UI_TEXT_TONE.eyebrow}`}>WELCOME</p>
            <h1 className={`mt-2 text-2xl font-black tracking-tight ${UI_TEXT_TONE.title}`}>
              呼び方を教えてください
            </h1>
            <p className={`mt-2 text-sm leading-6 ${UI_TEXT_TONE.body}`}>{helperText}</p>
          </div>

          {loading ? <p className="text-sm text-[#7B6861]">読み込み中です...</p> : null}
          {!loading && error ? <p className="mb-4 text-sm text-[#A1493E]">{error}</p> : null}

          {!loading ? (
            <>
              <label htmlFor="preferred-name" className="text-sm font-black text-[#5D4D46]">
                呼び方
              </label>
              <input
                id="preferred-name"
                type="text"
                value={preferredName}
                onChange={(event) => setPreferredName(event.target.value)}
                className="mt-3 w-full rounded-[1.1rem] border border-[#EADBC8] bg-[#FFFCFA] px-4 py-3 text-sm text-[#4A3E39] outline-none transition focus:border-[#D1907B] focus:bg-white"
                placeholder="例: 田中さん"
                autoFocus
                disabled={saving}
              />

              <button
                type="button"
                onClick={() => void handleSave()}
                disabled={!canSubmit}
                className="mt-5 w-full rounded-[1.6rem] bg-[#5D4D46] px-4 py-4 text-sm font-black text-[#FDF8F5] shadow-lg transition hover:bg-[#4A3E39] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {saving ? "保存中..." : "はじめる"}
              </button>
            </>
          ) : null}
        </div>
      </div>
    </main>
  );
}
